// client/src/views/Interactive_parts/InteractiveMapInternals.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import {
  useShapePathing,
  getShapeType,
} from '../../components/pathing_tools/PathingManager';

const IS_DEV = process.env.NODE_ENV === 'development';
const apiBase = IS_DEV ? 'https://beta.point-atlas.io/api/polygons' : '/api/polygons';

function useAnyModalOpen(excludeById = []) {
  const [anyOpen, setAnyOpen] = useState(false);

  useEffect(() => {
    const WIN = typeof window !== 'undefined' ? window : null;
    const DOC = typeof document !== 'undefined' ? document : null;
    if (!WIN || !DOC) return;

    const selector = [
      '[role="dialog"]',
      '[aria-modal="true"]',
      '.modal',
      '.ReactModal__Overlay',
      '.chakra-portal [role="dialog"]',
      '.mantine-Modal-root',
      '.MuiDialog-root',
      '.ant-modal-wrap',
      '.sweet-modal',
      '.modal-open',
      '.dialog-open',
      '.poi-info-modal',
      '.poi-edit-modal',
      '.poi-modal-backdrop'
    ].join(',');

    const isExcluded = (el) => {
      if (!el) return false;
      const id = el.getAttribute && el.getAttribute('id');
      if (id && excludeById.includes(id)) return true;
      let cur = el;
      while (cur) {
        const cid = cur.getAttribute && cur.getAttribute('id');
        if (cid && excludeById.includes(cid)) return true;
        cur = cur.parentElement;
      }
      return false;
    };

    const compute = () => {
      const nodes = Array.from(DOC.querySelectorAll(selector));
      const filtered = nodes.filter(n => !isExcluded(n));
      const visible = filtered.some(n => {
        const style = WIN.getComputedStyle(n);
        const hidden =
          style.display === 'none' ||
          style.visibility === 'hidden' ||
          style.opacity === '0';
        return !hidden;
      });
      setAnyOpen(!!visible);
    };

    const observer = new MutationObserver(() => compute());
    observer.observe(DOC.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden', 'aria-modal']
    });

    const poll = WIN.setInterval(compute, 500);
    compute();

    return () => {
      observer.disconnect();
      WIN.clearInterval(poll);
    };
  }, [excludeById.join('|')]);

  return anyOpen;
}

function resolveUploadsUrl(src) {
  if (!src) return '';
  const trimmed = String(src).trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('/')) {
    if (IS_DEV) return `https://beta.point-atlas.io${trimmed}`;
    return trimmed;
  }
  if (IS_DEV) return `https://beta.point-atlas.io/${trimmed}`;
  return `/${trimmed}`;
}

function ShapePathingRunner({
  shape,
  layer,
  enabled,
  controls = {},
  isHovered,
  showPoiLabels,
}) {
  const safeShape = shape || {};
  const shapeType = getShapeType(layer);
  const isClosedShape =
    shapeType === 'circle' ||
    shapeType === 'rectangle' ||
    shapeType === 'polygon';

  const behaviorType =
    safeShape.behaviorType ||
    safeShape.behavior_type ||
    'zone';

  const isPathingMode = behaviorType === 'pathing';

  const globalCtrl =
    (controls && (controls.__all || controls['*'] || controls.all)) || {};
  const perShapeCtrl =
    shape && controls && controls[shape.id]
      ? controls[shape.id]
      : {};

  const ctrlForShape = {
    ...globalCtrl,
    ...perShapeCtrl,
  };

  const hidden = !!ctrlForShape.hidden;

  const [pathConfig, setPathConfig] = useState(() => ({
    progress:
      typeof safeShape.tokenDefaultPosition === 'number'
        ? safeShape.tokenDefaultPosition
        : 0,
    speed:
      typeof safeShape.tokenSpeed === 'number'
        ? safeShape.tokenSpeed
        : 0.2,
    autoMove: !!safeShape.tokenAutoMove,
    direction:
      safeShape.tokenDirection ||
      (isClosedShape ? 'clockwise' : 'forward'),
  }));

  useEffect(() => {
    if (!shape) return;
    setPathConfig(prev => ({
      ...prev,
      progress:
        typeof safeShape.tokenDefaultPosition === 'number'
          ? safeShape.tokenDefaultPosition
          : 0,
      speed:
        typeof safeShape.tokenSpeed === 'number'
          ? safeShape.tokenSpeed
          : 0.2,
      autoMove: !!safeShape.tokenAutoMove,
      direction:
        safeShape.tokenDirection ||
        (isClosedShape ? 'clockwise' : 'forward'),
    }));
  }, [
    shape,
    safeShape.tokenDefaultPosition,
    safeShape.tokenSpeed,
    safeShape.tokenAutoMove,
    safeShape.tokenDirection,
    isClosedShape
  ]);

  const tokenIconUrl =
    safeShape.tokenIconUrl ||
    safeShape.token_icon_url ||
    '';

  const strokeColour =
    safeShape.strokeColor ||
    safeShape.stroke_color ||
    null;

  const tokenColor =
    strokeColour ||
    safeShape.tokenColor ||
    safeShape.token_color ||
    safeShape.fillColor ||
    '#ffe670';

  const paused = !!ctrlForShape.paused;
  const overriddenSpeed =
    typeof ctrlForShape.speed === 'number'
      ? ctrlForShape.speed
      : null;

  const overriddenProgress =
    typeof ctrlForShape.progress === 'number'
      ? ctrlForShape.progress
      : (typeof ctrlForShape.position === 'number'
          ? ctrlForShape.position
          : null);

  useEffect(() => {
    if (overriddenProgress === null) return;
    setPathConfig(prev => ({
      ...prev,
      progress: overriddenProgress,
    }));
  }, [overriddenProgress]);

  const effectiveAutoMove = paused ? false : pathConfig.autoMove;
  const effectiveSpeed =
    overriddenSpeed !== null ? overriddenSpeed : pathConfig.speed;

  const rawLabelFromMeta =
    (layer &&
      layer._shapeMeta &&
      (layer._shapeMeta.label || layer._shapeMeta.name)) ||
    safeShape.label ||
    safeShape.name ||
    (layer &&
      layer.options &&
      (layer.options.label || layer.options.name)) ||
    '';

  const shapeLabel =
    String(rawLabelFromMeta || '').trim() || 'Unnamed Zone';

  const effectiveEnabled = !!enabled && isPathingMode && !hidden;
  const showTokenLabels = effectiveEnabled && !!showPoiLabels;

  useShapePathing({
    layer,
    enabled: effectiveEnabled,
    autoMove: effectiveAutoMove,
    speed: effectiveSpeed,
    progress: pathConfig.progress,
    direction: isClosedShape
      ? pathConfig.direction || 'clockwise'
      : pathConfig.direction || 'forward',
    onConfigChange: setPathConfig,
    tokenIconUrl,
    tokenColor,
    shapeLabel,
    showLabels: showTokenLabels,
  });

  return null;
}

function GeomanControls({
  mapId,
  userId,
  onShapesChanged = () => {},
  onZonesChanged = () => {},
  onShapeClick,
  shapeLayerMap,
  onButtonClick,
  visibleZoneIds,
  canEdit,
  hoveredZoneId,
  onPathingEntriesChange,
  onGeomanActiveChange,
}) {
  const map = useMap();
  const shapesLayer = useRef(null);
  const allLayersById = useRef({});

  function wireTeleportClick(layer) {
    if (!layer) return;
    layer.off('click');
    layer.on('click', () => {
      const meta = layer._shapeMeta || {};
      const rawType = meta.behaviorType ?? meta.behavior_type ?? null;
      const behaviorType =
        typeof rawType === 'string' ? rawType.toLowerCase() : rawType;
      if (behaviorType !== 'teleport') return;

      const rawProps = meta.behaviorProps ?? meta.behavior_props ?? {};
      let props = rawProps;
      if (typeof rawProps === 'string') {
        try {
          props = JSON.parse(rawProps) || {};
        } catch {
          props = {};
        }
      } else if (!rawProps || typeof rawProps !== 'object') {
        props = {};
      }

      const destId =
        props.destinationMapId ??
        props.destination_map_id ??
        props.targetMapId ??
        props.target_map_id ??
        props.mapId ??
        props.map_id ??
        meta.teleportMapId ??
        meta.teleport_map_id ??
        null;

      const destUrl =
        props.targetUrl ??
        props.destinationUrl ??
        props.destination_url ??
        props.url ??
        null;

      if (destUrl) {
        window.location.href = String(destUrl);
      } else if (destId != null && destId !== '') {
        window.location.href = `/map/${destId}`;
      }
    });
  }

  useEffect(() => {
    if (!map || typeof onGeomanActiveChange !== 'function') return;

    const activate = () => onGeomanActiveChange(true);
    const deactivate = () => onGeomanActiveChange(false);

    if (canEdit) {
      map.on('pm:drawstart', activate);
      map.on('pm:drawend', deactivate);
      map.on('pm:editstart', activate);
      map.on('pm:editend', deactivate);
      map.on('pm:cutstart', activate);
      map.on('pm:cutend', deactivate);
    }

    return () => {
      map.off('pm:drawstart', activate);
      map.off('pm:drawend', deactivate);
      map.off('pm:editstart', activate);
      map.off('pm:editend', deactivate);
      map.off('pm:cutstart', activate);
      map.off('pm:cutend', deactivate);
      onGeomanActiveChange(false);
    };
  }, [map, canEdit, onGeomanActiveChange]);

  useEffect(() => {
    if (!mapId) return;
    fetch(`${apiBase}?mapId=${mapId}`)
      .then(res => res.json())
      .then(shapes => {
        if (shapesLayer.current) shapesLayer.current.clearLayers();
        else shapesLayer.current = L.featureGroup().addTo(map);

        if (shapeLayerMap && shapeLayerMap.current) shapeLayerMap.current = {};
        allLayersById.current = {};

        const entries = [];

        shapes.forEach(shapeRaw => {
          const shape = {
            ...shapeRaw,
            strokeColor: shapeRaw.strokeColor || shapeRaw.stroke_color || '#3388ff',
            fillColor: shapeRaw.fillColor || shapeRaw.fill_color || '#3388ff',
            strokeOpacity: (shapeRaw.stroke_opacity ?? 1.0) * 1,
            fillOpacity: (shapeRaw.fill_opacity ?? 0.3) * 1
          };

          let geometry;
          try {
            geometry = typeof shape.geometry === 'string'
              ? JSON.parse(shape.geometry)
              : shape.geometry;
          } catch {
            return;
          }

          let layer;
          if (
            geometry.type === 'Point' &&
            typeof shape.radius === 'number' &&
            geometry.coordinates
          ) {
            const [lng, lat] = geometry.coordinates;
            layer = L.circle([lat, lng], {
              radius: shape.radius,
              color: shape.strokeColor,
              fillColor: shape.fillColor,
              opacity: shape.strokeOpacity,
              fillOpacity: shape.fillOpacity,
              weight: 3
            });
          } else {
            const geojsonFeature = {
              type: 'Feature',
              geometry,
              properties: {},
            };
            const layers = L.geoJSON(geojsonFeature, {
              pointToLayer: (_f, latlng) =>
                L.circleMarker(latlng, {
                  radius: 10,
                  fillColor: shape.fillColor,
                  color: shape.strokeColor,
                  opacity: shape.strokeOpacity,
                  fillOpacity: shape.fillOpacity,
                  weight: 3
                }),
              style: {
                color: shape.strokeColor,
                fillColor: shape.fillColor,
                opacity: shape.strokeOpacity,
                fillOpacity: shape.fillOpacity,
                weight: 3
              }
            }).getLayers();
            layer = layers[0];
          }
          if (!layer) return;

          layer._dbId = shape.id;
          layer.options.color = shape.strokeColor;
          layer.options.fillColor = shape.fillColor;
          layer.options.opacity = shape.strokeOpacity;
          layer.options.fillOpacity = shape.fillOpacity;
          layer.options.label = shape.label;

          layer._shapeMeta = {
            ...shape,
            id: shape.id,
            geometry: shape.geometry,
            radius: shape.radius,
            shapeType: shape.shapeType,
            pattern: shape.pattern,
          };

          const meta = layer._shapeMeta || {};
          const behaviorType =
            meta.behaviorType ?? meta.behavior_type ?? null;

          if (behaviorType === 'pathing' && !canEdit) {
            if (typeof layer.setStyle === 'function') {
              layer.setStyle({
                color: 'transparent',
                fillColor: 'transparent',
                opacity: 0,
                fillOpacity: 0,
                weight: 0
              });
            }
            layer.options.color = 'transparent';
            layer.options.fillColor = 'transparent';
            layer.options.opacity = 0;
            layer.options.fillOpacity = 0;
          }

          if (canEdit && onShapeClick) {
            layer.on('click', () => {
              const base = layer._shapeMeta || shape;
              onShapeClick(
                {
                  ...base,
                  id: base.id,
                  strokeColor: layer.options.color,
                  fillColor: layer.options.fillColor,
                  strokeOpacity: layer.options.opacity,
                  fillOpacity: layer.options.fillOpacity,
                  label: base.label,
                  geometry: base.geometry,
                  radius: base.radius,
                  shapeType: base.shapeType,
                  pattern: base.pattern
                },
                layer
              );
            });
          } else {
            wireTeleportClick(layer);
          }

          if (shapeLayerMap && shapeLayerMap.current) {
            shapeLayerMap.current[shape.id] = layer;
          }
          allLayersById.current[shape.id] = layer;
          shapesLayer.current.addLayer(layer);

          entries.push({ id: shape.id, shape, layer });
        });

        if (onPathingEntriesChange) {
          onPathingEntriesChange(entries);
        }

        if (shapesLayer.current && !map.hasLayer(shapesLayer.current)) {
          shapesLayer.current.addTo(map);
        }
        onShapesChanged();
        onZonesChanged && onZonesChanged();
      })
      .catch(() => {});
  }, [mapId, map, canEdit]);

  useEffect(() => {
    if (!map) return;

    if (shapesLayer.current) {
      map.removeLayer(shapesLayer.current);
      shapesLayer.current = L.featureGroup().addTo(map);
    } else {
      shapesLayer.current = L.featureGroup().addTo(map);
    }

    map.pm.removeControls();

    if (canEdit) {
      map.pm.addControls({
        position: 'topleft',
        drawCircle: true,
        drawCircleMarker: false,
        drawMarker: false,
        drawPolyline: true,
        cutPolygon: false,
        dragMode: false,
        editMode: false,
        removalMode: false,
        drawRectangle: true,
        drawPolygon: true,
        rotateMode: false
      });

      function handleButtonClick(e) {
        onButtonClick && onButtonClick(e.btnName);
      }

      map.on('pm:buttonclick', handleButtonClick);

      map.on('pm:create', async (e) => {
        const layer = e.layer;
        const isCircle = typeof layer.getRadius === 'function';
        let geometry;
        let radius = null;
        let shapeType = '';

        if (isCircle) {
          const center = layer.getLatLng();
          geometry = {
            type: 'Point',
            coordinates: [center.lng, center.lat],
          };
          radius = layer.getRadius();
          shapeType = 'Circle';
        } else {
          geometry = layer.toGeoJSON().geometry;
          shapeType = geometry.type || '';
        }

        const payload = {
          mapId,
          userId,
          geometry,
          radius,
          shapeType,
          fillColor: layer.options.fillColor || '#3388ff',
          strokeColor: layer.options.color || '#3388ff',
          strokeOpacity: layer.options.opacity ?? 1.0,
          fillOpacity: layer.options.fillOpacity ?? 0.3,
          label: 'Unnamed Zone',
          pattern: ''
        };

        try {
          const res = await fetch(apiBase, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          const data = await res.json();
          layer._dbId = data.id;

          if (shapeLayerMap && shapeLayerMap.current) {
            shapeLayerMap.current[data.id] = layer;
          }
          allLayersById.current[data.id] = layer;

          const newShape = {
            ...data,
            strokeColor: data.strokeColor || data.stroke_color || payload.fillColor,
            fillColor: data.fillColor || data.fill_color || payload.fillColor,
            strokeOpacity:
              (data.stroke_opacity ?? payload.strokeOpacity) ??
              (layer.options.opacity ?? 1.0),
            fillOpacity:
              (data.fill_opacity ?? payload.fillOpacity) ??
              (layer.options.fillOpacity ?? 0.3),
            geometry: payload.geometry,
            radius: payload.radius,
            shapeType: payload.shapeType,
            label: payload.label,
            pattern: payload.pattern,
          };

          layer._shapeMeta = {
            ...newShape,
          };

          shapesLayer.current.addLayer(layer);

          if (onShapeClick) {
            layer.on('click', () => {
              const base = layer._shapeMeta || newShape;
              onShapeClick(
                {
                  ...base,
                  id: base.id,
                  strokeColor: layer.options.color,
                  fillColor: layer.options.fillColor,
                  strokeOpacity: layer.options.opacity,
                  fillOpacity: layer.options.fillOpacity,
                  label: base.label,
                  geometry: base.geometry,
                  radius: base.radius,
                  shapeType: base.shapeType,
                  pattern: base.pattern
                },
                layer
              );
            });
          } else {
            wireTeleportClick(layer);
          }

          if (onPathingEntriesChange) {
            onPathingEntriesChange(prev =>
              Array.isArray(prev)
                ? [...prev, { id: data.id, shape: newShape, layer }]
                : [{ id: data.id, shape: newShape, layer }]
            );
          }

          if (layer.pm && typeof layer.pm.enable === 'function') {
            const isLineGeometry =
              !isCircle &&
              geometry &&
              (geometry.type === 'LineString' ||
                geometry.type === 'MultiLineString');
            layer.pm.enable({ allowSelfIntersection: isLineGeometry });
          }
          onZonesChanged && onZonesChanged();
        } catch {
          // ignore
        }
      });

      map.on('pm:edit', async (e) => {
        e.layers.eachLayer(async layer => {
          if (!layer._dbId) return;
          const isCircle = typeof layer.getRadius === 'function';
          let geometry;
          let radius = null;
          let shapeType = '';

          if (isCircle) {
            const center = layer.getLatLng();
            geometry = {
              type: 'Point',
              coordinates: [center.lng, center.lat],
            };
            radius = layer.getRadius();
            shapeType = 'Circle';
          } else {
            geometry = layer.toGeoJSON().geometry;
            shapeType = geometry.type || '';
          }

          const meta = layer._shapeMeta || {};
          const resolvedBehaviorType =
            meta.behaviorType ?? meta.behavior_type ?? null;
          const resolvedBehaviorProps =
            meta.behaviorProps ?? meta.behavior_props ?? undefined;
          const resolvedBehaviorState =
            meta.behaviorState ?? meta.behavior_state ?? undefined;

          const payload = {
            geometry,
            radius,
            shapeType,
            fillColor: layer.options.fillColor,
            strokeColor: layer.options.color,
            strokeOpacity: layer.options.opacity,
            fillOpacity: layer.options.fillOpacity,
            label: layer.options.label || 'Unnamed Zone',
            pattern: '',
            behaviorType: resolvedBehaviorType,
            behavior_type: resolvedBehaviorType,
            behaviorProps: resolvedBehaviorProps,
            behavior_props: resolvedBehaviorProps,
            behaviorState: resolvedBehaviorState,
            behavior_state: resolvedBehaviorState,
          };

          try {
            await fetch(`${apiBase}/${layer._dbId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
            });

            if (layer._shapeMeta) {
              layer._shapeMeta = {
                ...layer._shapeMeta,
                ...payload,
              };
            }

            if (onPathingEntriesChange) {
              onPathingEntriesChange(prev =>
                Array.isArray(prev)
                  ? prev.map(entry =>
                      entry.id === layer._dbId
                        ? {
                            ...entry,
                            shape: {
                              ...entry.shape,
                              geometry,
                              radius,
                              shapeType,
                              strokeColor: layer.options.color,
                              fillColor: layer.options.fillColor,
                              strokeOpacity: layer.options.opacity,
                              fillOpacity: layer.options.fillOpacity,
                              label: layer.options.label || 'Unnamed Zone',
                              behaviorType: resolvedBehaviorType,
                              behavior_type: resolvedBehaviorType,
                              behaviorProps: resolvedBehaviorProps,
                              behavior_props: resolvedBehaviorProps,
                              behaviorState: resolvedBehaviorState,
                              behavior_state: resolvedBehaviorState,
                            },
                          }
                        : entry
                    )
                  : prev
              );
            }

            if (!canEdit) {
              wireTeleportClick(layer);
            }

            onZonesChanged && onZonesChanged();
          } catch {
            // ignore
          }
        });
      });

      map.on('pm:remove', async (e) => {
        if (!e.layer._dbId) return;
        try {
          await fetch(`${apiBase}/${e.layer._dbId}`, { method: 'DELETE' });
          if (shapeLayerMap && shapeLayerMap.current) {
            delete shapeLayerMap.current[e.layer._dbId];
          }
          delete allLayersById.current[e.layer._dbId];
          shapesLayer.current.removeLayer(e.layer);

          if (onPathingEntriesChange) {
            onPathingEntriesChange(prev =>
              Array.isArray(prev)
                ? prev.filter(entry => entry.id !== e.layer._dbId)
                : prev
            );
          }

          onZonesChanged && onZonesChanged();
        } catch {
          // ignore
        }
      });

      return () => {
        map.off('pm:create');
        map.off('pm:edit');
        map.off('pm:remove');
        map.off('pm:buttonclick', handleButtonClick);
        if (shapesLayer.current) map.removeLayer(shapesLayer.current);
      };
    } else {
      map.pm.removeControls();
      map.off('pm:create');
      map.off('pm:edit');
      map.off('pm:remove');
      map.off('pm:buttonclick');
    }
  }, [map, mapId, userId, onZonesChanged, canEdit]);

  useEffect(() => {
    if (!map) return;
    Object.entries(allLayersById.current).forEach(([id, layer]) => {
      const isVisible = visibleZoneIds ? visibleZoneIds.has(Number(id)) : true;
      if (isVisible) {
        if (!map.hasLayer(layer)) map.addLayer(layer);
      } else {
        if (map.hasLayer(layer)) map.removeLayer(layer);
      }
    });
  }, [visibleZoneIds, map]);

  return null;
}

function MapFitter({ imageSize, mapId, resetTrigger }) {
  const map = useMap();

  const fitToImage = useCallback(
    (animate) => {
      if (!map || !imageSize) {
        return;
      }

      const bounds = L.latLngBounds([[0, 0], [imageSize[0], imageSize[1]]]);

      map.stop();
      map.closePopup();
      map.setMaxBounds(bounds);
      map.invalidateSize();
      map.fitBounds(bounds, { padding: [0, 0], animate: !!animate });
    },
    [map, imageSize]
  );

  useEffect(() => {
    fitToImage(false);
  }, [fitToImage, mapId]);

  useEffect(() => {
    if (!resetTrigger) return;
    fitToImage(true);
  }, [resetTrigger, fitToImage]);

  return null;
}

export {
  apiBase,
  useAnyModalOpen,
  resolveUploadsUrl,
  ShapePathingRunner,
  GeomanControls,
  MapFitter,
};
